import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { AlertTriangle, CheckCircle2, Loader2, RefreshCw, Copy, XCircle } from "lucide-react";

const emergentMessage = (issues) =>
  "My Your Varsity Application admin console reports these problems that auto-fix could not solve. Please identify the root cause and fix:\n\n" +
  issues.map((i, n) => `${n + 1}. [${i.area}] ${i.title} (seen ${i.count}x, last ${new Date(i.last_seen).toLocaleString()})\n   ${i.detail || ""}`).join("\n");

export function SystemHealthBanner({ onOpen }) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    const load = () => api.get("/admin/system/issues").then(({ data }) => setCount(data.open.length)).catch(() => {});
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, []);
  if (!count) return null;
  return (
    <button onClick={onOpen} data-testid="system-health-banner"
      className="mt-6 w-full flex items-center justify-between gap-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-left">
      <span className="flex items-center gap-2 text-sm font-semibold text-red-700"><AlertTriangle className="w-4 h-4" /> {count} system problem{count > 1 ? "s" : ""} detected</span>
      <span className="text-xs font-semibold text-red-700 underline">View & fix</span>
    </button>
  );
}

export function SystemHealthPanel() {
  const [data, setData] = useState({ open: [], resolved: [] });
  const [fixing, setFixing] = useState(false);
  const [result, setResult] = useState(null);
  const load = () => api.get("/admin/system/issues").then(({ data: d }) => setData(d)).catch(() => {});
  useEffect(() => { load(); }, []);

  const autoFix = async () => {
    setFixing(true);
    try {
      const { data: r } = await api.post("/admin/system/auto-fix", {}, { timeout: 120000 });
      setResult(r);
      if (r.remaining.length) toast.warning(`${r.cleared} cleared. ${r.remaining.length} still need fixing in Emergent.`);
      else toast.success("All systems healthy.");
      load();
    } catch (e) { toast.error("Auto-fix could not run. The server may be down."); }
    finally { setFixing(false); }
  };
  const resolve = async (id) => { await api.post(`/admin/system/issues/${id}/resolve`); load(); };
  const copy = () => { navigator.clipboard.writeText(emergentMessage(data.open)); toast.success("Copied — paste it into Emergent chat."); };

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm" data-testid="system-health-panel">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="font-display font-bold text-[#0B2447]">System health</h3>
          <p className="text-sm text-slate-500">Problems are recorded automatically. Press Run auto-fix to reconnect services and reload settings.</p>
        </div>
        <Button onClick={autoFix} disabled={fixing} data-testid="system-autofix-btn" className="bg-[#0B2447] hover:bg-[#153461]">
          {fixing ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <RefreshCw className="w-4 h-4 mr-1.5" />} Run auto-fix
        </Button>
      </div>

      {result && (
        <div className="mt-4 grid sm:grid-cols-2 gap-2" data-testid="autofix-results">
          {result.checks.map((c) => (
            <div key={c.name} className={`rounded-lg border px-3 py-2 text-sm ${c.ok ? "border-emerald-200 bg-emerald-50" : "border-red-200 bg-red-50"}`}>
              <div className="flex items-center gap-1.5 font-semibold">{c.ok ? <CheckCircle2 className="w-4 h-4 text-emerald-600" /> : <XCircle className="w-4 h-4 text-red-600" />}{c.name}</div>
              <div className="text-xs text-slate-600 mt-0.5 break-words">{c.detail}</div>
            </div>
          ))}
        </div>
      )}

      <div className="mt-5">
        <div className="flex items-center justify-between">
          <p className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500">Open problems ({data.open.length})</p>
          {data.open.length > 0 && <Button variant="outline" size="sm" onClick={copy} data-testid="copy-emergent-btn"><Copy className="w-3.5 h-3.5 mr-1" /> Copy for Emergent</Button>}
        </div>
        {data.open.length === 0 && <p className="text-sm text-emerald-700 mt-2 flex items-center gap-1.5" data-testid="system-healthy"><CheckCircle2 className="w-4 h-4" /> No open problems.</p>}
        <div className="mt-2 space-y-2">
          {data.open.map((i) => (
            <div key={i.id} className="rounded-lg border border-red-100 bg-red-50/50 p-3" data-testid={`issue-${i.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-red-800">{i.title} <span className="text-xs font-normal text-red-600">· {i.area} · {i.count}x</span></p>
                  {i.detail && <p className="text-xs text-slate-600 mt-0.5 break-words">{i.detail}</p>}
                  <p className="text-xs text-slate-500 mt-1"><span className="font-semibold">What to do:</span> {i.hint}</p>
                  <p className="text-[10px] text-slate-400 mt-1">Last seen {new Date(i.last_seen).toLocaleString()}</p>
                </div>
                <Button variant="ghost" size="sm" onClick={() => resolve(i.id)} data-testid={`resolve-issue-${i.id}`}>Mark fixed</Button>
              </div>
            </div>
          ))}
        </div>
        {result && result.remaining.length > 0 && (
          <p className="mt-3 text-sm text-red-700" data-testid="needs-emergent">Auto-fix could not solve {result.remaining.length} problem(s). Press <b>Copy for Emergent</b> and paste it into Emergent chat so it can be fixed in the code.</p>
        )}
      </div>
    </div>
  );
}
